import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  Form,
  Space,
  Tag,
  Tooltip,
  message,
  Empty,
  Input,
  Spin,
  Typography,
  Grid,
  Switch,
  Alert,
  Checkbox
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
  TeamOutlined,
  MailOutlined,
  ReloadOutlined,
  SendOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WhatsAppOutlined
} from '@ant-design/icons';
import eventAPI from '../../services/eventAPI';
import axios from 'axios';
import { BASE_URL } from '../../Constants';
import { useTranslation } from 'react-i18next';
import PhoneField from '../shared/PhoneField';




const { TextArea } = Input;
const { Text } = Typography;
const { useBreakpoint } = Grid;

// Loose check only (digits in any spelling, spaces, +, -, brackets); the API normalizes the number.
const PHONE_PATTERN = /^[+\d\u0660-\u0669\u06F0-\u06F9\s\-()]{7,20}$/;

/**
 * Organizer Management Component (Email Invitation Flow)
 * 
 * NEW FLOW:
 * 1. Admin enters email + name
 * 2. API creates organizer in eventOrganizers container
 * 3. Email sent with password setup link
 * 4. Organizer clicks link, sets password
 * 5. Organizer can now login
 */
const OrganizerManagement = ({
  eventId,
  isNewEvent = false,
  onPendingInvitesChange,
  hasGates = true,
  // Events that offer a guest contact (weddings always, general events when switched on): an
  // organizer can be the guests' WhatsApp contact on the event page.
  offersGuestContact = false
}) => {
  const { t } = useTranslation();
  const screens = useBreakpoint();
  // Below the md breakpoint, secondary data moves into an expandable row
  const isMobile = screens.md === false;
  // State
  const [organizers, setOrganizers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [pendingInvites, setPendingInvites] = useState([]);
  const [editingOrganizer, setEditingOrganizer] = useState(null);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editForm] = Form.useForm();
  // Whether invited organizers may create/edit/delete this event's gates (backend default: on).
  // This only covers gate CRUD; organizers can always open assigned gates and check guests in.
  const [organizerGateControl, setOrganizerGateControl] = useState(true);
  const [gateControlSaving, setGateControlSaving] = useState(false);

  // Fetch organizers when component mounts or eventId changes
  useEffect(() => {
    if (eventId) {
      fetchOrganizers();
      fetchOrganizerSettings();
    }
  }, [eventId]);

  /**
   * Load the "organizers can manage gates" switch
   */
  const fetchOrganizerSettings = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Organizer/event/${eventId}/settings`);
      setOrganizerGateControl(response.data?.organizerCanManageGates !== false);
    } catch (error) {
      console.error('Error fetching organizer settings:', error);
    }
  };

  /**
   * Save the switch immediately (it is an event setting, not part of the event form payload)
   */
  const handleGateControlChange = async (checked) => {
    setGateControlSaving(true);
    try {
      const response = await axios.put(`${BASE_URL}/Organizer/event/${eventId}/settings`, {
        organizerCanManageGates: checked
      });
      setOrganizerGateControl(response.data?.organizerCanManageGates !== false);
      message.success(
        t(checked
          ? 'organizerManagement.gateControl.enabledMsg'
          : 'organizerManagement.gateControl.disabledMsg')
      );
    } catch (error) {
      console.error('Error saving organizer settings:', error);
      message.error(error.response?.data?.message || t('organizerManagement.gateControl.saveFailed'));
    } finally {
      setGateControlSaving(false);
    }
  };

  /**
   * Fetch organizers from API
   */
  const fetchOrganizers = async () => {
    if (!eventId) return;

    setLoading(true);
    try {
      // Call API: GET /api/Organizer/event/{eventId}/organizers
      const token = localStorage.getItem('accessToken');
      // const response = await axios.get(`${BASE_URL}/Organizer/event/${eventId}/organizers`, {
      //   headers: { Authorization: `Bearer ${token}` }
      // });
      const response = await axios.get(`${BASE_URL}/Organizer/event/${eventId}/organizers`);
      setOrganizers(response.data || []);
    } catch (error) {
      console.error('Error fetching organizers:', error);
      // Don't show error if 404 (no organizers yet)
      if (error.response?.status !== 404) {
        message.error(t('organizerManagement.messages.loadFailed'));
      }
      setOrganizers([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Get status tag color and icon based on invitation status
   */
  const getStatusTag = (record) => {
    const status = record.invitationStatus?.toLowerCase() || 'pending';
    // Stage 1: Pending (not yet sent)
    if (status === 'pending_save') {
      return (
        <Tooltip title={t('organizerManagement.status.pendingSaveTooltip')}>
          <Tag icon={<ClockCircleOutlined />} color="orange">
            {t('organizerManagement.status.pending')}
          </Tag>
        </Tooltip>
      );
    }
    // Stage 2: Awaiting Acceptance (sent, not accepted)
    if (status === 'pending') {
      return (
        <Tooltip title={t('organizerManagement.status.awaitingAcceptanceTooltip')}>
          <Tag icon={<MailOutlined />} color="processing">
            {t('organizerManagement.status.awaitingAcceptance')}
          </Tag>
        </Tooltip>
      );
    }
    // Stage 3: Accepted
    if (status === 'accepted' || record.isActive) {
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          {t('organizerManagement.status.accepted')}
        </Tag>
      );
    }
    // Stage 4: Expired
    if (status === 'expired') {
      return (
        <Tag icon={<ExclamationCircleOutlined />} color="error">
          {t('organizerManagement.status.expired')}
        </Tag>
      );
    }
  };

  // Table columns
  const columns = [
    {
      title: t('organizerManagement.columns.organizer'),
      dataIndex: 'userEmail',
      key: 'userEmail',
      width: isMobile ? undefined : 200,
      render: (email, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <UserOutlined style={{ fontSize: '16px', color: '#666' }} />
          <div style={isMobile ? { minWidth: 0, wordBreak: 'break-word' } : { whiteSpace: 'nowrap' }}>
            <div><strong>{record.fullName || t('organizerManagement.notSet')}</strong></div>
            <div style={{ fontSize: '12px', color: '#666' }}>{email}</div>
            {offersGuestContact && record.phoneNumber && (
              <div dir="ltr" style={{ fontSize: '12px', color: '#666', textAlign: 'start' }}>{record.phoneNumber}</div>
            )}
            {offersGuestContact && record.isGuestContact && (
              <Tooltip title={t('organizerManagement.guestContact.tagTooltip')}>
                <Tag icon={<WhatsAppOutlined />} color="green" style={{ marginTop: 4 }}>
                  {t('organizerManagement.guestContact.tag')}
                </Tag>
              </Tooltip>
            )}
            {isMobile && <div style={{ marginTop: 4 }}>{getStatusTag(record)}</div>}
          </div>
        </div>
      )
    },
    {
      title: t('organizerManagement.columns.status'),
      key: 'status',
      width: 120,
      responsive: ['md'],
      render: (_, record) => getStatusTag(record)
    },
    {
      title: t('organizerManagement.columns.invited'),
      dataIndex: 'invitationSentAt',
      key: 'invitationSentAt',
      width: 110,
      responsive: ['md'],
      render: (date) => date ? new Date(date).toLocaleDateString() : '—'
    },
    {
      title: t('organizerManagement.columns.accepted'),
      dataIndex: 'acceptedAt',
      key: 'acceptedAt',
      width: 110,
      responsive: ['md'],
      render: (date) => date ? new Date(date).toLocaleDateString() : '—'
    },
    {
      title: t('organizerManagement.columns.notes'),
      dataIndex: 'notes',
      key: 'notes',
      ellipsis: true,
      responsive: ['md'],
      render: (notes) => notes || <span style={{ color: '#ccc' }}>—</span>
    },
    {
      title: t('organizerManagement.columns.actions'),
      key: 'actions',
      width: isMobile ? 104 : 130,
      render: (_, record) => (
        <Space size={isMobile ? 0 : 8} style={{ whiteSpace: 'nowrap' }}>
          {/* Pending invites - can only remove from list */}
          {record.invitationStatus === 'pending_save' ? (
            <Tooltip title={t('organizerManagement.actionsTooltips.removeFromPending')}>
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => {
                  const updated = pendingInvites.filter(inv => inv.id !== record.id);
                  setPendingInvites(updated);
                  if (onPendingInvitesChange) {
                    onPendingInvitesChange(updated);
                  }
                  message.success(t('organizerManagement.messages.removedFromPending'));
                }}
              />
            </Tooltip>
          ) : (
            <>
              {/* Edit Button */}
              <Tooltip title={t('organizerManagement.actionsTooltips.editOrganizer')}>
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  onClick={() => handleEdit(record)}
                />
              </Tooltip>

              {/* Make this organizer the one guests can message (needs a number) */}
              {offersGuestContact && record.phoneNumber && !record.isGuestContact && (
                <Tooltip title={t('organizerManagement.guestContact.makeContact')}>
                  <Button
                    type="text"
                    icon={<WhatsAppOutlined />}
                    onClick={() => handleMakeGuestContact(record)}
                  />
                </Tooltip>
              )}

              {/* Resend invitation for pending organizers */}
              {(record.invitationStatus === 'pending' || record.invitationStatus === 'expired') && (
                <Tooltip title={t('organizerManagement.actionsTooltips.resendInvitation')}>
                  <Button
                    type="text"
                    icon={<SendOutlined />}
                    onClick={() => handleResendInvite(record)}
                  />
                </Tooltip>
              )}

              {/* Delete Button */}
              <Tooltip title={t('organizerManagement.actionsTooltips.removeOrganizer')}>
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDelete(record.id)}
                />
              </Tooltip>
            </>
          )}
        </Space>
      )
    }
  ];

  /**
   * Handle delete/remove organizer
   */
  const handleDelete = (organizerId) => {
    Modal.confirm({
      title: t('organizerManagement.confirmRemove.title'),
      content: t('organizerManagement.confirmRemove.content'),
      okText: t('organizerManagement.confirmRemove.okText'),
      okType: 'danger',
      onOk: async () => {
        try {
          // Call DELETE API
          await axios.delete(`${BASE_URL}/Organizer/${organizerId}`);

          // Update local state
          setOrganizers(prev => prev.filter(o => o.id !== organizerId));
          message.success(t('organizerManagement.messages.removeSuccess'));
        } catch (error) {
          console.error('Error removing organizer:', error);
          const errorMessage = error.response?.data?.message || t('organizerManagement.messages.removeFailed');
          message.error(errorMessage);
        }
      }
    });
  };


  const handleEdit = (record) => {
    setEditingOrganizer(record);
    editForm.setFieldsValue({
      fullName: record.fullName,
      notes: record.notes,
      phoneNumber: record.phoneNumber || '',
      isGuestContact: !!record.isGuestContact
    });
    setIsEditModalVisible(true);
  };

  const handleEditSubmit = async () => {
    try {
      const values = await editForm.validateFields();

      // Call UPDATE API
      await axios.put(
        `${BASE_URL}/Organizer/${editingOrganizer.id}`,
        {
          fullName: values.fullName,
          notes: values.notes,
          // Only events offering a guest contact show these fields; otherwise null keeps whatever is stored.
          ...(offersGuestContact
            ? { phoneNumber: values.phoneNumber?.trim() || '', isGuestContact: !!values.isGuestContact }
            : {})
        }
      );
      message.success(t('organizerManagement.messages.updateSuccess'));
      setIsEditModalVisible(false);
      editForm.resetFields();
      setEditingOrganizer(null);
      fetchOrganizers(); // Refresh the list
    } catch (error) {
      console.error('Error updating organizer:', error);
      const errorMessage = error.response?.data?.message || t('organizerManagement.messages.updateFailed');
      message.error(errorMessage);
    }
  };

  const handleMakeGuestContact = async (record) => {
    try {
      await axios.put(`${BASE_URL}/Organizer/${record.id}/guest-contact`);
      message.success(t('organizerManagement.guestContact.contactUpdated', { name: record.fullName || record.userEmail }));
      fetchOrganizers();
    } catch (error) {
      console.error('Error setting guest contact:', error);
      message.error(error.response?.data?.message || t('organizerManagement.messages.updateFailed'));
    }
  };

  /**
   * Handle resend invitation
   */
  const handleResendInvite = async (record) => {
    try {
      await axios.post(
        `${BASE_URL}/Organizer/event/${eventId}/invite`,
        {
          email: record.userEmail,
          fullName: record.fullName,
          notes: record.notes || ''
        }
      );
      message.success(t('organizerManagement.messages.resendSuccess', { email: record.userEmail }));
      fetchOrganizers(); // Refresh the organizers list
    } catch (error) {
      console.error('Error resending invitation:', error);
      const errorMessage = error.response?.data?.message || t('organizerManagement.messages.resendFailed');
      message.error(errorMessage);
    }
  };
  /**
   * Open invite modal
   */
  const handleAdd = () => {
    form.resetFields();
    const hasGuestContact = [...organizers, ...pendingInvites].some((o) => o.isGuestContact);
    form.setFieldsValue({ isGuestContact: offersGuestContact && !hasGuestContact });
    setIsModalVisible(true);
  };

  /**
   * Submit invitation - sends email to organizer
   */
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setInviting(true);
      // NEW EVENT: Add to pending list instead of calling API
      if (isNewEvent || !eventId) {
        const pendingInvite = {
          id: Date.now().toString(), // Temporary ID
          userEmail: values.email,
          fullName: values.fullName,
          notes: values.notes,
          phoneNumber: offersGuestContact ? values.phoneNumber?.trim() || null : null,
          isGuestContact: offersGuestContact && !!values.isGuestContact,
          isPending: true,
          invitationStatus: 'pending_save',
          invitationSentAt: null
        };
        const newPendingList = [...pendingInvites, pendingInvite];
        setPendingInvites(newPendingList);

        // Notify parent component
        if (onPendingInvitesChange) {
          onPendingInvitesChange(newPendingList);
        }
        message.success(
          t('organizerManagement.messages.pendingAdded', { name: values.fullName }),
          5
        );
        setIsModalVisible(false);
        form.resetFields();
        setInviting(false);
        return;
      }
      // EXISTING EVENT: Call API immediately (existing behavior)
      const response = await axios.post(
        `${BASE_URL}/Organizer/event/${eventId}/invite`,
        {
          email: values.email,
          fullName: values.fullName,
          notes: values.notes,
          ...(offersGuestContact
            ? { phoneNumber: values.phoneNumber?.trim() || null, isGuestContact: !!values.isGuestContact }
            : {})
        }
      );
      message.success(t('organizerManagement.messages.inviteSuccess', { email: values.email }));
      setIsModalVisible(false);
      form.resetFields();
      fetchOrganizers();
    } catch (error) {
      console.error('Error inviting organizer:', error);
      const errorMessage = error.response?.data?.message || t('organizerManagement.messages.inviteFailed');
      message.error(errorMessage);
    } finally {
      setInviting(false);
    }
  };
  // WhatsApp number + "show to guests" fields, shared by the invite and edit forms (events offering a guest contact only).
  const guestContactFields = offersGuestContact && (
    <>
      <Form.Item
        name="phoneNumber"
        label={t('organizerManagement.guestContact.phoneLabel')}
        extra={t('organizerManagement.guestContact.phoneExtra')}
        dependencies={['isGuestContact']}
        rules={[
          { pattern: PHONE_PATTERN, message: t('organizerManagement.guestContact.invalidPhone') },
          // The organizer shown to guests must have a number, otherwise guests get no WhatsApp button.
          ({ getFieldValue }) => ({
            validator: (_, value) =>
              getFieldValue('isGuestContact') && !value?.trim()
                ? Promise.reject(new Error(t('organizerManagement.guestContact.phoneRequiredForGuests')))
                : Promise.resolve()
          })
        ]}
      >
        <PhoneField size="large" />
      </Form.Item>
      <Form.Item
        name="isGuestContact"
        valuePropName="checked"
        extra={t('organizerManagement.guestContact.showToGuestsExtra')}
      >
        <Checkbox>{t('organizerManagement.guestContact.showToGuests')}</Checkbox>
      </Form.Item>
    </>
  );

  /**
   * Cancel and close modal
   */
  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  return (
    <div>
      <Card styles={{ body: { padding: isMobile ? 12 : 24 } }}>
        {/* Own header (not Card title/extra) so the buttons wrap under the title on narrow screens */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            marginBottom: 16
          }}
        >
          <Space style={{ fontSize: 16, fontWeight: 600 }}>
            <TeamOutlined />
            {t('organizerManagement.cardTitle')}
          </Space>
          <Space wrap>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchOrganizers}
              loading={loading}
            >
              {t('organizerManagement.refresh')}
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              {t('organizerManagement.inviteOrganizer')}
            </Button>
          </Space>
        </div>
        {eventId && !isNewEvent && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 12,
              marginBottom: 16,
              padding: 12,
              backgroundColor: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 4
            }}
          >
            <div style={{ flex: '1 1 240px', minWidth: 0 }}>
              <div style={{ fontWeight: 500 }}>{t('organizerManagement.gateControl.title')}</div>
              <div style={{ fontSize: 12, color: '#666' }}>
                {t('organizerManagement.gateControl.description')}
              </div>
            </div>
            <Switch
              checked={organizerGateControl}
              loading={gateControlSaving}
              onChange={handleGateControlChange}
            />
          </div>
        )}

        {eventId && !isNewEvent && !hasGates && !organizerGateControl && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('organizerManagement.gateControl.noGatesWarning')}
          />
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (organizers && organizers.length > 0) || (pendingInvites && pendingInvites.length > 0) ? (
          <Table
            dataSource={[...pendingInvites, ...organizers]}
            columns={columns}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={isMobile ? undefined : { x: 'max-content' }}
            expandable={isMobile ? {
              expandedRowRender: (record) => (
                <div style={{ fontSize: 12, lineHeight: 1.9 }}>
                  <div>
                    <strong>{t('organizerManagement.columns.invited')}:</strong>{' '}
                    {record.invitationSentAt ? new Date(record.invitationSentAt).toLocaleDateString() : '—'}
                  </div>
                  <div>
                    <strong>{t('organizerManagement.columns.accepted')}:</strong>{' '}
                    {record.acceptedAt ? new Date(record.acceptedAt).toLocaleDateString() : '—'}
                  </div>
                  <div>
                    <strong>{t('organizerManagement.columns.notes')}:</strong>{' '}
                    {record.notes || '—'}
                  </div>
                </div>
              )
            } : undefined}
          />
        ) : (
          <Empty
            description={t('organizerManagement.noOrganizers')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              {t('organizerManagement.inviteFirstOrganizer')}
            </Button>
          </Empty>
        )}

        {/* Info box */}
        <div style={{ marginTop: 16, padding: 12, backgroundColor: '#f0f5ff', borderRadius: 4 }}>
          <Space direction="vertical" size="small" style={{ fontSize: '12px', color: '#666' }}>
            <div><strong>{t('organizerManagement.howItWorks.title')}</strong></div>
            <div>• {t('organizerManagement.howItWorks.line1')}</div>
            <div>• {t('organizerManagement.howItWorks.line2')}</div>
          </Space>
        </div>
      </Card>
      {/* Pending invites warning banner */}
      {pendingInvites.length > 0 && (
        <div style={{
          marginTop: 16,
          padding: 12,
          backgroundColor: '#fff7e6',
          borderRadius: 4,
          border: '1px solid #ffd591'
        }}>
          <Space direction="vertical" size="small">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClockCircleOutlined style={{ color: '#fa8c16', fontSize: 16 }} />
              <strong style={{ color: '#d46b08' }}>
                {t('organizerManagement.pendingBanner.title', { count: pendingInvites.length })}
              </strong>
            </div>
            <div style={{ fontSize: 12, color: '#8c8c8c' }}>
              {t('organizerManagement.pendingBanner.description')}
            </div>
          </Space>
        </div>
      )}
      {/* Invite Modal */}
      <Modal
        title={
          <Space>
            <MailOutlined />
            {t('organizerManagement.inviteModal.title')}
          </Space>
        }
        open={isModalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        width={500}
        okText={t('organizerManagement.inviteModal.sendInvitation')}
        okButtonProps={{
          loading: inviting,
          icon: <SendOutlined />
        }}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            name="email"
            label={t('organizerManagement.inviteModal.emailLabel')}
            rules={[
              { required: true, message: t('organizerManagement.messages.enterEmail') },
              { type: 'email', message: t('organizerManagement.messages.enterValidEmail') }
            ]}
            extra={t('organizerManagement.inviteModal.emailExtra')}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={t('organizerManagement.inviteModal.emailPlaceholder')}
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="fullName"
            label={t('organizerManagement.inviteModal.fullNameLabel')}
            rules={[
              { required: true, message: t('organizerManagement.messages.enterName') }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t('organizerManagement.inviteModal.fullNamePlaceholder')}
              size="large"
            />
          </Form.Item>

          {guestContactFields}

          <Form.Item
            name="notes"
            label={t('organizerManagement.inviteModal.notesLabel')}
            extra={t('organizerManagement.inviteModal.notesExtra')}
          >
            <TextArea
              rows={3}
              placeholder={t('organizerManagement.inviteModal.notesPlaceholder')}
            />
          </Form.Item>
        </Form>

        <div style={{
          marginTop: 16,
          padding: 12,
          backgroundColor: '#fff7e6',
          borderRadius: 4,
          border: '1px solid #ffd591'
        }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('organizerManagement.inviteModal.note')}
          </Text>
        </div>
      </Modal>
      {/* Edit Organizer Modal */}
      <Modal
        title={
          <Space>
            <EditOutlined />
            {t('organizerManagement.editModal.title')}
          </Space>
        }
        open={isEditModalVisible}
        onOk={handleEditSubmit}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
          setEditingOrganizer(null);
        }}
        width={500}
        okText={t('organizerManagement.editModal.update')}
      >
        <Form
          form={editForm}
          layout="vertical"
        >
          <Form.Item
            label={t('organizerManagement.editModal.emailLabel')}
            extra={t('organizerManagement.editModal.emailExtra')}
          >
            <Input
              prefix={<MailOutlined />}
              value={editingOrganizer?.userEmail}
              disabled
              size="large"
            />
          </Form.Item>

          <Form.Item
            name="fullName"
            label={t('organizerManagement.editModal.fullNameLabel')}
            rules={[
              { required: true, message: t('organizerManagement.messages.enterName') }
            ]}
          >
            <Input
              prefix={<UserOutlined />}
              placeholder={t('organizerManagement.editModal.fullNamePlaceholder')}
              size="large"
            />
          </Form.Item>

          {guestContactFields}

          <Form.Item
            name="notes"
            label={t('organizerManagement.editModal.notesLabel')}
            extra={t('organizerManagement.editModal.notesExtra')}
          >
            <TextArea
              rows={3}
              placeholder={t('organizerManagement.editModal.notesPlaceholder')}
            />
          </Form.Item>
        </Form>

        <div style={{
          marginTop: 16,
          padding: 12,
          backgroundColor: '#fff7e6',
          borderRadius: 4,
          border: '1px solid #ffd591'
        }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {t('organizerManagement.editModal.note')}
          </Text>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizerManagement;